// mvp-returns Phase H9 — Propose Template create/edit Drawer

import {
  Box, Button, Drawer, Group, NumberInput,
  Select, Stack, Textarea, TextInput, Title
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProposeTemplateApi } from "../../apis";
import type { IProposeTemplate, IProposeTemplateWriteInput } from "../../apis/propose-template";
import { message } from "../../utils/message";
import { PermissionButton } from "../ui";

type TFn = (k: string, o?: Record<string, unknown>) => string;

// ── Select options (match backend constants) ──

const shippingOptions = (t: TFn) => [
  { value: "BUYER_DELIVERS",       label: t("templateForm.shipping.BUYER_DELIVERS") },
  { value: "SELLER_PICKS_UP",      label: t("templateForm.shipping.SELLER_PICKS_UP") },
  { value: "BUYER_SHIPS",          label: t("templateForm.shipping.BUYER_SHIPS") },
  { value: "SELLER_PAYS_SHIPPING", label: t("templateForm.shipping.SELLER_PAYS_SHIPPING") },
  { value: "NOT_REQUIRED",         label: t("templateForm.shipping.NOT_REQUIRED") },
];

const resolutionOptions = (t: TFn) => [
  { value: "REFUND_MONEY",        label: t("templateForm.resolution.REFUND_MONEY") },
  { value: "REPLACEMENT",         label: t("templateForm.resolution.REPLACEMENT") },
  { value: "NEXT_ORDER_DISCOUNT", label: t("templateForm.resolution.NEXT_ORDER_DISCOUNT") },
  { value: "NONE",                label: t("templateForm.resolution.NONE") },
  { value: "MIXED",               label: t("templateForm.resolution.MIXED") },
];

const refundMethodOptions = (t: TFn) => [
  { value: "BANK_TRANSFER",     label: t("templateForm.refundMethod.BANK_TRANSFER") },
  { value: "INVOICE_DEDUCTION", label: t("templateForm.refundMethod.INVOICE_DEDUCTION") },
  { value: "CASH",              label: t("templateForm.refundMethod.CASH") },
  { value: "CREDIT_BALANCE",    label: t("templateForm.refundMethod.CREDIT_BALANCE") },
];

// ── Form type ──

type FormValues = {
  name: string;
  description: string;
  refundPercent: number;
  deductionReason: string;
  shippingResponsibility: string;
  shippingCost: number | "";
  shippingNotes: string;
  resolutionType: string;
  refundMethod: string;
  proposalNotesTemplate: string;
};

function toFormValues(t?: IProposeTemplate | null): FormValues {
  return {
    name                  : t?.name ?? "",
    description           : t?.description ?? "",
    refundPercent         : t?.refundPercent ?? 100,
    deductionReason       : t?.deductionReason ?? "",
    shippingResponsibility: t?.shippingResponsibility ?? "BUYER_DELIVERS",
    shippingCost          : t?.shippingCost ?? "",
    shippingNotes         : t?.shippingNotes ?? "",
    resolutionType        : t?.resolutionType ?? "REFUND_MONEY",
    refundMethod          : t?.refundMethod ?? "INVOICE_DEDUCTION",
    proposalNotesTemplate : t?.proposalNotesTemplate ?? "",
  };
}

function toWriteInput(values: FormValues): IProposeTemplateWriteInput {
  return {
    name                  : values.name.trim(),
    description           : values.description.trim() || null,
    refundPercent         : values.refundPercent,
    deductionReason       : values.deductionReason.trim() || null,
    shippingResponsibility: values.shippingResponsibility,
    shippingCost          : values.shippingCost !== "" ? Number(values.shippingCost) : null,
    shippingNotes         : values.shippingNotes.trim() || null,
    resolutionType        : values.resolutionType,
    refundMethod          : values.resolutionType === "REFUND_MONEY"
      ? (values.refundMethod || null)
      : null,
    proposalNotesTemplate : values.proposalNotesTemplate.trim() || null,
  };
}

// ── Props ──

interface Props {
  opened: boolean;
  template?: IProposeTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}

export function TemplateFormDrawer({ opened, template, onClose, onSaved }: Props) {
  const { t } = useTranslation("returns");
  const SHIPPING_OPTIONS = shippingOptions(t);
  const RESOLUTION_OPTIONS = resolutionOptions(t);
  const REFUND_METHOD_OPTIONS = refundMethodOptions(t);
  const isEdit = !!template;
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    initialValues: toFormValues(template),
    validate: {
      name                  : (v) => (!v.trim() ? t("templateForm.nameRequired") : null),
      shippingResponsibility: (v) => (!v ? t("templateForm.shippingRequired") : null),
      resolutionType        : (v) => (!v ? t("templateForm.resolutionRequired") : null),
      refundMethod          : (v, values) =>
        values.resolutionType === "REFUND_MONEY" && !v ? t("templateForm.refundMethodRequired") : null,
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues(toFormValues(template));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, template]);

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const payload = toWriteInput(values);
      if (isEdit && template) {
        await ProposeTemplateApi.update(template.id, payload);
      } else {
        await ProposeTemplateApi.create(payload);
      }
      message.success(isEdit ? t("templateForm.updated") : t("templateForm.created"));
      onSaved();
      onClose();
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t("templateForm.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={720}
      title={
        <Title order={4}>
          {isEdit ? t("templateForm.editTitle") : t("templateForm.newTitle")}
        </Title>
      }
    >
      <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label={t("templateForm.name")}
            placeholder={t("templateForm.namePlaceholder")}
            required
            {...form.getInputProps("name")}
          />

          <Textarea
            label={t("templateForm.description")}
            placeholder={t("templateForm.descriptionPlaceholder")}
            autosize
            minRows={2}
            {...form.getInputProps("description")}
          />

          <NumberInput
            label={t("bulkAction.refundPercent")}
            min={0}
            max={100}
            required
            {...form.getInputProps("refundPercent")}
          />

          {form.values.refundPercent < 100 && (
            <Textarea
              label={t("bulkAction.deductionReason")}
              placeholder={t("templateForm.deductionPlaceholder")}
              autosize
              minRows={2}
              {...form.getInputProps("deductionReason")}
            />
          )}

          <Select
            label={t("templateForm.shippingResponsibility")}
            data={SHIPPING_OPTIONS}
            required
            {...form.getInputProps("shippingResponsibility")}
          />

          <NumberInput
            label={t("templateForm.shippingCost")}
            placeholder="0"
            min={0}
            thousandSeparator=","
            {...form.getInputProps("shippingCost")}
          />

          <Textarea
            label={t("templateForm.shippingNotes")}
            autosize
            minRows={2}
            {...form.getInputProps("shippingNotes")}
          />

          <Select
            label={t("resolutionPanel.resolutionTypeTitle")}
            data={RESOLUTION_OPTIONS}
            required
            {...form.getInputProps("resolutionType")}
          />

          {form.values.resolutionType === "REFUND_MONEY" && (
            <Select
              label={t("templateForm.refundMethodLabel")}
              data={REFUND_METHOD_OPTIONS}
              required
              {...form.getInputProps("refundMethod")}
            />
          )}

          <Textarea
            label={t("templateForm.proposalNotesTemplate")}
            description={t("templateForm.proposalNotesDesc")}
            autosize
            minRows={3}
            {...form.getInputProps("proposalNotesTemplate")}
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} type="button">
              {t("templateForm.close")}
            </Button>
            <PermissionButton permission="RETURN_PROPOSE" type="submit" loading={submitting}>
              {isEdit ? t("resolutionPanel.save") : t("templateForm.create")}
            </PermissionButton>
          </Group>
        </Stack>
      </Box>
    </Drawer>
  );
}
